import { useEffect, useState } from "react";

interface Coords {
  lat: number;
  lng: number;
}

interface UseGeolocationResult {
  coords: Coords | null;
  status: "idle" | "pending" | "resolved" | "denied" | "unavailable";
}

interface UseGeolocationOptions {
  enabled?: boolean;
  timeoutMs?: number;
}

export function useGeolocation(
  options: UseGeolocationOptions = {}
): UseGeolocationResult {
  const { enabled = true, timeoutMs = 5000 } = options;
  const [coords, setCoords] = useState<Coords | null>(null);
  const [status, setStatus] = useState<UseGeolocationResult["status"]>(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return "unavailable";
    }
    return "idle";
  });

  useEffect(() => {
    if (!enabled) return;
    if (status === "unavailable") return;
    let cancelled = false;
    setStatus("pending");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus("resolved");
      },
      () => {
        if (cancelled) return;
        setStatus("denied");
      },
      { timeout: timeoutMs, maximumAge: 5 * 60 * 1000 }
    );
    return () => {
      cancelled = true;
    };
    // status intentionally omitted: it's derived above and we only re-run on
    // enabled/timeout changes, not on status transitions within the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, timeoutMs]);

  return { coords, status };
}
